import type { Vec3 } from '../../../../geo/types';
import { orientedFace, planPoint } from '../../solids';
import { gableWall, rectWall, roofUv } from '../masonry';
import { transverseRoof } from '../gothic/roofs';
import { fittedButtress } from '../oudeKerk/oudeKerkButtress';
import { bayWindows, lancet, LIGHTS, type NieuweKerkBuild } from './nieuweKerkParts';

/**
 * The transept: the south arm and the raised bay of the north arm at the height of the vessel it
 * crosses, closed to the south by the great gable and for now to the north by a plain wall over the
 * two low bays of the old arm, which the painting shows (nieuwekerk-scan, frame x 1150-1195, rows
 * 570-600) and De Gids 1942 describes (p. 29). The south arm stood before 1452 and is of tuff; the
 * north arm is brick banded with stone (p. 31). The buttresses' size is not recorded; they stand at the
 * bays as far out as the trace leaves room for.
 */
const BUTTRESS = { width: 0.9, depth: 1.4, drop: 1.0, pinnacle: 1.8 } as const;

/*
 * The side walls of the south arm, of tuff, and of the raised bay of the north arm, of brick, each
 * with a window to a bay; the vessel's walls close the crossing between them.
 */
function buildSideWalls(s: NieuweKerkBuild): void {
  const t = s.plan.transept;
  const v = s.plan.vessel;
  const raised = [v.v1, t.fullNorth];
  const side = (u: number, from: number, to: number, styles: NieuweKerkBuild['stone'], bounds: readonly number[]): void => {
    const windows = bayWindows(bounds, from, to > from ? 1 : -1, LIGHTS.clerestory);
    rectWall(s.builder, styles, s.frame, [[u, from], [u, to]], [0, t.eaveY], windows);
  };
  side(t.u1, t.south, v.v0, s.stone, t.southBays);
  side(t.u1, v.v1, t.fullNorth, s.brick, raised);
  side(t.u0, t.fullNorth, v.v1, s.brick, raised);
  side(t.u0, v.v0, t.south, s.stone, t.southBays);
}

/*
 * The arms' slopes from their gables to the crossing's centre: the arms are as wide and as high as the
 * vessel, so the valleys run on the crossing's diagonals, and the vessel's slopes take the rest.
 */
function buildRoof(s: NieuweKerkBuild): void {
  const t = s.plan.transept;
  const v = s.plan.vessel;
  const uc = (t.u0 + t.u1) / 2;
  const eave = (u: number, w: number): Vec3 => planPoint(u, t.eaveY, w);
  const ridge = (w: number): Vec3 => planPoint(uc, t.ridgeY, w);
  const centre = ridge(s.plan.vc);
  const face = (points: readonly Vec3[], outward: Vec3, uv: ReturnType<typeof roofUv>): void => {
    orientedFace(s.builder, s.stone.roof, s.frame, points, outward, uv);
  };
  const east = roofUv(eave(t.u1, t.south), eave(t.u1, t.fullNorth), ridge(t.fullNorth));
  face([eave(t.u1, t.south), eave(t.u1, v.v0), centre, ridge(t.south)], [1, 1, 0], east);
  face([eave(t.u1, v.v1), eave(t.u1, t.fullNorth), ridge(t.fullNorth), centre], [1, 1, 0], east);
  const west = roofUv(eave(t.u0, t.fullNorth), eave(t.u0, t.south), ridge(t.south));
  face([eave(t.u0, t.fullNorth), eave(t.u0, v.v1), centre, ridge(t.fullNorth)], [-1, 1, 0], west);
  face([eave(t.u0, v.v0), eave(t.u0, t.south), ridge(t.south), centre], [-1, 1, 0], west);
}

function buildBody(s: NieuweKerkBuild): void {
  const t = s.plan.transept;
  const width = t.u1 - t.u0;
  const heights = { eaveY: t.eaveY, apexY: t.ridgeY };
  gableWall(s.builder, s.stone, s.frame, [[t.u0, t.south], [t.u1, t.south]], heights, [lancet(width / 2, LIGHTS.transept), lancet(width / 2, LIGHTS.portal)]);
  gableWall(s.builder, s.brick, s.frame, [[t.u1, t.fullNorth], [t.u0, t.fullNorth]], heights);
  buildSideWalls(s);
  buildRoof(s);
}

function buildLowArm(s: NieuweKerkBuild): void {
  const t = s.plan.transept;
  const l = t.low;
  const width = t.u1 - t.u0;
  const heights = { eaveY: l.eaveY, apexY: l.ridgeY };
  const bays = t.northBays.slice(1);
  gableWall(s.builder, s.brick, s.frame, [[t.u1, l.v1], [t.u0, l.v1]], heights, [lancet(width / 2, LIGHTS.lowFront)]);
  gableWall(s.builder, s.brick, s.frame, [[t.u0, l.v0], [t.u1, l.v0]], heights);
  rectWall(s.builder, s.brick, s.frame, [[t.u1, l.v0], [t.u1, l.v1]], [0, l.eaveY], bayWindows(bays, l.v0, 1, LIGHTS.aisle));
  rectWall(s.builder, s.brick, s.frame, [[t.u0, l.v1], [t.u0, l.v0]], [0, l.eaveY], bayWindows(bays, l.v1, -1, LIGHTS.aisle));
  transverseRoof(s.builder, s.stone, s.frame, { u0: t.u0, u1: t.u1, v0: l.v0, v1: l.v1 }, l);
}

/** Buttresses on the arms' side walls at their bays and at the corners of the fronts. */
function buildButtresses(s: NieuweKerkBuild): void {
  const t = s.plan.transept;
  const high = { width: BUTTRESS.width, depth: BUTTRESS.depth, height: t.eaveY - BUTTRESS.drop, pinnacle: BUTTRESS.pinnacle };
  const low = { ...high, height: t.low.eaveY - BUTTRESS.drop };
  const half = BUTTRESS.width / 2;
  for (const v of t.southBays.slice(1, -1)) {
    fittedButtress(s, [t.u0, v], [-1, 0], high);
    fittedButtress(s, [t.u1, v], [1, 0], high);
  }
  for (const u of [t.u0 + half, t.u1 - half]) fittedButtress(s, [u, t.south], [0, -1], high);
  fittedButtress(s, [t.u1, t.low.v0], [1, 0], high);
  fittedButtress(s, [t.u0, t.low.v0], [-1, 0], high);
  for (const u of [t.u0 + half, t.u1 - half]) fittedButtress(s, [u, t.low.v1], [0, 1], low);
}

export function buildTransept(s: NieuweKerkBuild): void {
  buildBody(s);
  buildLowArm(s);
  buildButtresses(s);
}
